import { Button, Group, Select, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import {
  PermissionEntryLevelEnum,
  PermissionEntrySubjectEnum,
  UpdateMemberPermissionsDtoPresetEnum,
} from 'src/Services/API/generated';

import styles from './PermissionsEditor.module.scss';
import { PermissionsState, Props } from './PermissionsEditor.types';

// Permission subjects with their display order
// Note: Video uses same permissions as AssetStorage, CommunityFeed is not asset-level
export const PERMISSION_SUBJECTS = [
  PermissionEntrySubjectEnum.Contact,
  PermissionEntrySubjectEnum.Event,
  PermissionEntrySubjectEnum.Expense,
  PermissionEntrySubjectEnum.BankAccount,
  PermissionEntrySubjectEnum.MoneyTransfer,
  PermissionEntrySubjectEnum.Project,
  PermissionEntrySubjectEnum.Task,
  PermissionEntrySubjectEnum.Checklist,
  PermissionEntrySubjectEnum.Instruction,
  PermissionEntrySubjectEnum.PracticalInformation,
  PermissionEntrySubjectEnum.AssetStorage,
  PermissionEntrySubjectEnum.EntityExport,
  PermissionEntrySubjectEnum.FinanceAdmin,
  PermissionEntrySubjectEnum.Dashboard,
  PermissionEntrySubjectEnum.Cartography,
  PermissionEntrySubjectEnum.DeductibleCharges,
] as const;

// Preset configurations
const PRESET_LEVELS: Record<UpdateMemberPermissionsDtoPresetEnum, PermissionEntryLevelEnum> = {
  [UpdateMemberPermissionsDtoPresetEnum.Viewer]: PermissionEntryLevelEnum.Read,
  [UpdateMemberPermissionsDtoPresetEnum.Contributor]: PermissionEntryLevelEnum.Own,
  [UpdateMemberPermissionsDtoPresetEnum.Manager]: PermissionEntryLevelEnum.Full,
};

export const buildPresetPermissions = (
  preset: UpdateMemberPermissionsDtoPresetEnum
): PermissionsState =>
  PERMISSION_SUBJECTS.reduce(
    (acc, subject) => ({
      ...acc,
      [subject]: PRESET_LEVELS[preset],
    }),
    {} as PermissionsState
  );

export const usePermissionSubjectLabels = (): Record<
  (typeof PERMISSION_SUBJECTS)[number],
  string
> => {
  const { t } = useTranslation();
  return {
    [PermissionEntrySubjectEnum.Contact]: t('permissions.subject.contact', 'Contacts'),
    [PermissionEntrySubjectEnum.Event]: t('permissions.subject.event', 'Événements'),
    [PermissionEntrySubjectEnum.Expense]: t('permissions.subject.expense', 'Dépenses'),
    [PermissionEntrySubjectEnum.BankAccount]: t(
      'permissions.subject.bankAccount',
      'Comptes bancaires'
    ),
    [PermissionEntrySubjectEnum.MoneyTransfer]: t('permissions.subject.moneyTransfer', 'Virements'),
    [PermissionEntrySubjectEnum.Project]: t('permissions.subject.project', 'Projets'),
    [PermissionEntrySubjectEnum.Task]: t('permissions.subject.task', 'Tâches'),
    [PermissionEntrySubjectEnum.Checklist]: t('permissions.subject.checklist', 'Checklists'),
    [PermissionEntrySubjectEnum.Instruction]: t('permissions.subject.instruction', 'Consignes'),
    [PermissionEntrySubjectEnum.PracticalInformation]: t(
      'permissions.subject.practicalInformation',
      'Infos pratiques'
    ),
    [PermissionEntrySubjectEnum.AssetStorage]: t(
      'permissions.subject.assetStorage',
      'Fichiers et vidéos'
    ),
    [PermissionEntrySubjectEnum.EntityExport]: t('permissions.subject.entityExport', 'Exports'),
    [PermissionEntrySubjectEnum.FinanceAdmin]: t(
      'permissions.subject.financeAdmin',
      'Admin budget'
    ),
    [PermissionEntrySubjectEnum.Dashboard]: t('permissions.subject.dashboard', 'Tableau de bord'),
    [PermissionEntrySubjectEnum.Cartography]: t(
      'permissions.subject.cartography',
      'Cartographie (étages, pièces, lits)'
    ),
    [PermissionEntrySubjectEnum.DeductibleCharges]: t(
      'permissions.subject.deductibleCharges',
      'Charges déductibles'
    ),
  };
};

// Descriptions spécifiques par type d'entité et niveau
const getLevelDescriptions = (): Record<
  (typeof PERMISSION_SUBJECTS)[number],
  Record<PermissionEntryLevelEnum, string>
> => ({
  [PermissionEntrySubjectEnum.Contact]: {
    [PermissionEntryLevelEnum.None]: 'Aucun accès aux contacts',
    [PermissionEntryLevelEnum.Read]: 'Peut consulter les contacts',
    [PermissionEntryLevelEnum.Own]: 'Peut ajouter et modifier ses contacts',
    [PermissionEntryLevelEnum.Full]: 'Peut gérer tous les contacts',
  },
  [PermissionEntrySubjectEnum.Event]: {
    [PermissionEntryLevelEnum.None]: 'Aucun accès aux événements',
    [PermissionEntryLevelEnum.Read]: 'Peut consulter les événements',
    [PermissionEntryLevelEnum.Own]: 'Peut créer et modifier ses événements',
    [PermissionEntryLevelEnum.Full]: 'Peut gérer tous les événements',
  },
  [PermissionEntrySubjectEnum.Expense]: {
    [PermissionEntryLevelEnum.None]: 'Aucun accès aux dépenses',
    [PermissionEntryLevelEnum.Read]: 'Peut consulter les dépenses',
    [PermissionEntryLevelEnum.Own]: 'Peut ajouter et modifier ses dépenses',
    [PermissionEntryLevelEnum.Full]: 'Peut gérer toutes les dépenses',
  },
  [PermissionEntrySubjectEnum.BankAccount]: {
    [PermissionEntryLevelEnum.None]: 'Aucun accès aux comptes',
    [PermissionEntryLevelEnum.Read]: 'Peut consulter les comptes',
    [PermissionEntryLevelEnum.Own]: 'Peut ajouter et modifier ses comptes',
    [PermissionEntryLevelEnum.Full]: 'Peut gérer tous les comptes',
  },
  [PermissionEntrySubjectEnum.MoneyTransfer]: {
    [PermissionEntryLevelEnum.None]: 'Aucun accès aux virements',
    [PermissionEntryLevelEnum.Read]: 'Peut consulter les virements',
    [PermissionEntryLevelEnum.Own]: 'Peut ajouter et modifier ses virements',
    [PermissionEntryLevelEnum.Full]: 'Peut gérer tous les virements',
  },
  [PermissionEntrySubjectEnum.Project]: {
    [PermissionEntryLevelEnum.None]: 'Aucun accès aux projets',
    [PermissionEntryLevelEnum.Read]: 'Peut consulter et voter',
    [PermissionEntryLevelEnum.Own]: 'Peut créer et modifier ses projets',
    [PermissionEntryLevelEnum.Full]: 'Peut gérer tous les projets',
  },
  [PermissionEntrySubjectEnum.Task]: {
    [PermissionEntryLevelEnum.None]: 'Aucun accès aux actions',
    [PermissionEntryLevelEnum.Read]: 'Peut consulter les actions',
    [PermissionEntryLevelEnum.Own]: 'Peut créer et modifier ses actions',
    [PermissionEntryLevelEnum.Full]: 'Peut gérer toutes les actions',
  },
  [PermissionEntrySubjectEnum.Checklist]: {
    [PermissionEntryLevelEnum.None]: 'Aucun accès aux checklists',
    [PermissionEntryLevelEnum.Read]: 'Peut consulter les checklists',
    [PermissionEntryLevelEnum.Own]: 'Peut créer et compléter ses checklists',
    [PermissionEntryLevelEnum.Full]: 'Peut gérer toutes les checklists',
  },
  [PermissionEntrySubjectEnum.Instruction]: {
    [PermissionEntryLevelEnum.None]: 'Aucun accès aux consignes',
    [PermissionEntryLevelEnum.Read]: 'Peut consulter les consignes',
    [PermissionEntryLevelEnum.Own]: 'Peut créer et modifier ses consignes',
    [PermissionEntryLevelEnum.Full]: 'Peut gérer toutes les consignes',
  },
  [PermissionEntrySubjectEnum.PracticalInformation]: {
    [PermissionEntryLevelEnum.None]: 'Aucun accès aux infos pratiques',
    [PermissionEntryLevelEnum.Read]: 'Peut consulter les infos pratiques',
    [PermissionEntryLevelEnum.Own]: 'Peut ajouter et modifier ses infos',
    [PermissionEntryLevelEnum.Full]: 'Peut gérer toutes les infos pratiques',
  },
  [PermissionEntrySubjectEnum.AssetStorage]: {
    [PermissionEntryLevelEnum.None]: 'Aucun accès aux fichiers et vidéos',
    [PermissionEntryLevelEnum.Read]: 'Peut télécharger et visionner',
    [PermissionEntryLevelEnum.Own]: 'Peut déposer et gérer ses fichiers',
    [PermissionEntryLevelEnum.Full]: 'Peut gérer tous les fichiers',
  },
  [PermissionEntrySubjectEnum.EntityExport]: {
    [PermissionEntryLevelEnum.None]: 'Aucun accès aux exports',
    [PermissionEntryLevelEnum.Read]: 'Peut télécharger les exports',
    [PermissionEntryLevelEnum.Own]: 'Peut créer et télécharger ses exports',
    [PermissionEntryLevelEnum.Full]: 'Peut gérer tous les exports',
  },
  [PermissionEntrySubjectEnum.FinanceAdmin]: {
    [PermissionEntryLevelEnum.None]: 'Pas de gestion du budget',
    [PermissionEntryLevelEnum.Read]: 'Pas de gestion du budget',
    [PermissionEntryLevelEnum.Own]: 'Pas de gestion du budget',
    [PermissionEntryLevelEnum.Full]: 'Peut définir le budget plafond annuel',
  },
  [PermissionEntrySubjectEnum.Dashboard]: {
    [PermissionEntryLevelEnum.None]: 'Aucun accès au tableau de bord',
    [PermissionEntryLevelEnum.Read]: 'Peut consulter le tableau de bord',
    [PermissionEntryLevelEnum.Own]: 'Peut consulter le tableau de bord',
    [PermissionEntryLevelEnum.Full]: 'Peut consulter le tableau de bord',
  },
  [PermissionEntrySubjectEnum.Cartography]: {
    [PermissionEntryLevelEnum.None]: 'Aucun accès à la cartographie',
    [PermissionEntryLevelEnum.Read]: 'Peut consulter la cartographie',
    [PermissionEntryLevelEnum.Own]: 'Peut créer et modifier ses éléments',
    [PermissionEntryLevelEnum.Full]: 'Peut gérer toute la cartographie',
  },
  [PermissionEntrySubjectEnum.DeductibleCharges]: {
    [PermissionEntryLevelEnum.None]: 'Aucun accès aux charges déductibles',
    [PermissionEntryLevelEnum.Read]: 'Peut consulter ses charges déductibles',
    [PermissionEntryLevelEnum.Own]: 'Peut consulter ses charges déductibles',
    [PermissionEntryLevelEnum.Full]: 'Peut consulter ses charges déductibles',
  },
});

const PermissionsEditor = ({ value, onChange }: Props) => {
  const { t } = useTranslation();
  const subjectLabels = usePermissionSubjectLabels();
  const descriptions = getLevelDescriptions();

  const getLevelOptionsForSubject = (subject: (typeof PERMISSION_SUBJECTS)[number]) => [
    {
      value: PermissionEntryLevelEnum.None,
      label: t('permissions.level.none', 'Aucun accès'),
      description: descriptions[subject][PermissionEntryLevelEnum.None],
    },
    {
      value: PermissionEntryLevelEnum.Read,
      label: t('permissions.level.read', 'Consultation'),
      description: descriptions[subject][PermissionEntryLevelEnum.Read],
    },
    {
      value: PermissionEntryLevelEnum.Own,
      label: t('permissions.level.own', 'Contributeur'),
      description: descriptions[subject][PermissionEntryLevelEnum.Own],
    },
    {
      value: PermissionEntryLevelEnum.Full,
      label: t('permissions.level.full', 'Gestionnaire'),
      description: descriptions[subject][PermissionEntryLevelEnum.Full],
    },
  ];

  const renderSelectOption = ({
    option,
  }: {
    option: { value: string; label: string; description?: string };
  }) => (
    <Group gap="xs" wrap="nowrap">
      <div>
        <Text size="sm" fw={500}>
          {option.label}
        </Text>
        {option.description && (
          <Text size="xs" c="dimmed">
            {option.description}
          </Text>
        )}
      </div>
    </Group>
  );

  const handlePresetClick = (preset: UpdateMemberPermissionsDtoPresetEnum) => {
    onChange(buildPresetPermissions(preset));
  };

  const handlePermissionChange = (
    subject: PermissionEntrySubjectEnum,
    level: PermissionEntryLevelEnum
  ) => {
    onChange({ ...value, [subject]: level });
  };

  return (
    <>
      <div className={styles.presetsSection}>
        <div className={styles.presetsLabel}>
          {t('permissions.presets.label', 'Appliquer un profil type')}
        </div>
        <div className={styles.presetButtons}>
          <Button
            variant="light"
            size="xs"
            onClick={() => handlePresetClick(UpdateMemberPermissionsDtoPresetEnum.Viewer)}
          >
            {t('permissions.presets.viewer', 'Visiteur')}
          </Button>
          <Button
            variant="light"
            size="xs"
            onClick={() => handlePresetClick(UpdateMemberPermissionsDtoPresetEnum.Contributor)}
          >
            {t('permissions.presets.contributor', 'Contributeur')}
          </Button>
          <Button
            variant="light"
            size="xs"
            onClick={() => handlePresetClick(UpdateMemberPermissionsDtoPresetEnum.Manager)}
          >
            {t('permissions.presets.manager', 'Gestionnaire')}
          </Button>
        </div>
      </div>

      <div className={styles.permissionsSection}>
        <div className={styles.permissionsLabel}>
          {t('permissions.detailed.label', 'Permissions détaillées')}
        </div>
        {PERMISSION_SUBJECTS.map(subject => (
          <div key={subject} className={styles.permissionRow}>
            <div className={styles.permissionName}>{subjectLabels[subject]}</div>
            <Select
              className={styles.permissionSelect}
              size="sm"
              data={getLevelOptionsForSubject(subject)}
              value={value[subject]}
              onChange={selected =>
                handlePermissionChange(subject, selected as PermissionEntryLevelEnum)
              }
              renderOption={renderSelectOption}
            />
          </div>
        ))}
      </div>
    </>
  );
};

export default PermissionsEditor;
